<!--
	Obrazovka na prácu s klientmi kampane - hovor, dáta
-->
<script lang="ts">
	import type { Connection } from "../libs/sip/connections";
	import type { DialerAgentConfiguration } from "../libs/app/configuration";
	import type { Target } from "../libs/app/target";
	import type { TargetAlternate } from "../libs/app/target-alternate";

	import CallAlternate from "../components/TargetAlternate.svelte";
	import TimeScheduled from "../components/TimeScheduled.svelte";
	import secondsToTime from "../libs/time/format";
	import { DialerAgentStatus } from "../libs/app/dialer-agent-enums";
	import { afterUpdate, onDestroy, onMount } from "svelte";
	import { api, dialerAgent } from "../bootstrap";
	import { confirmation } from "../components/Confirmation";
	import { get, Writable } from "svelte/store";
	import { navigate, useParams } from "svelte-navigator";
	import { toast } from "@zerodevx/svelte-toast";

	const params = useParams();

	const configuration: Writable<DialerAgentConfiguration> = dialerAgent.configuration;
	const currentConnection: Writable<Connection> = dialerAgent.currentConnection;

	let unsubscribeParams: () => void;
	let unsubscribeDialerAgentStatus: () => void;
	let modal: Function | false = false;
	let modalSaveCallback: Function = null;

	let targetId: number = null;
	let target: Target = null;
	let caller: string = null;

	let callReceived = false;
	let dialerAgentStatus: DialerAgentStatus;
	let afterCallWorkTimeExceedeed = false;
	let enabledAfterCallWorkTime: string;

	let callDurationInterval = null;
	let callDuration: number = 0;
	let formattedCallDuration = "00:00:00";
	$: formattedCallDuration = secondsToTime(callDuration);

	let afterCallWorkDurationInterval = null;
	let afterCallWorkDuration: number = 0;
	let formattedAfterCallWorkDuration = "00:00:00";
	$: formattedAfterCallWorkDuration = secondsToTime(afterCallWorkDuration);

	let volume = 0.5;
	$: setVolume(volume);

	let callScriptColWidth: number;
	let formColWidth: number;
	let crmColWidth: number;
	let containerClass: string;

	let setDefaultStatus = true;

	let timeScheduled: string = null;
	let targetAlternate: TargetAlternate = null;

	let callIsDeclined = false;
	let error = false;

	let disabledAnswerMachine = false;
	let hangupCause = null;

	let paused = false;

	toggleTargetStatus(true);

	/**
	 * Prijatie hovoru
	 */
	function answer() {
		if ($currentConnection.sipClient.sessionExists() === false) {
			error = true;
			navigate("/main/summary");
			return;
		}

		$currentConnection.sipClient.answer().catch((e) => {
			toast.push("Pri prijímaní hovoru došlo k chybe.");
			toast.push(e.message);
			error = true;
			navigate("/main/summary");
			return;
		});

		const t = setTimeout(() => {
			disabledAnswerMachine = true;
			clearTimeout(t);
		}, 15000);

		paused = true;
		api.pause();
	}

	/**
	 * Odmietnutie hovoru
	 */
	function decline() {
		if ($currentConnection.sipClient.sessionExists() === false) {
			return;
		}

		$currentConnection.sipClient
			.decline()
			.then(() => {
				callIsDeclined = true;
				navigate("/main/summary");
			})
			.catch((err) => {
				toast.push("Pri odmietnutí hovoru došlo k chybe.");
				toast.push(err.message);
				error = true;
				navigate("/main/summary");
			});
	}

	/**
	 * Ukončenie hovoru
	 */
	function hangup(cause: string = null) {
		if ($currentConnection.sipClient.sessionExists() === false) {
			return;
		}

		hangupCause = cause;
		$currentConnection.sipClient.hangup().catch(() => {
			toast.push("Pri ukončení hovoru došlo k chybe.");
			error = true;
		});
	}

	async function answeringMachine(e: Event) {
		e.preventDefault();

		const result = await confirmation.show("<p>Naozaj chcete ukončit prácu s klientom kampane?</p><p>Dáta budú uložené automaticky.</p>");

		if (result === true) {
			setTargetStatus("8"); // Odkazovač
			hangup("answeringMachine");
			navigate("/main/summary");
		}
	}

	/**
	 * Uloženie dát o klientovi
	 *
	 * @param e
	 */
	async function send() {
		const form = document.getElementById("target-data") as HTMLFormElement;

		if (form) {
			const formData = new FormData(form);
			const targetOutputData = Object.fromEntries(formData) as Object;
			if (callIsDeclined === true) {
				targetOutputData["__status__"] = "0";
			}
			targetOutputData["__target_alternate__"] = targetAlternate;
			targetOutputData["__time_scheduled__"] = timeScheduled;
			if (timeScheduled !== null) {
				targetOutputData["__scheduled_exten__"] = $currentConnection.exten;
			} else {
				targetOutputData["__scheduled_exten__"] = null;
			}

			targetOutputData["__cpe__"] = $currentConnection.cpe;
			targetOutputData["__exten__"] = $currentConnection.exten;
			targetOutputData["__acw_duration__"] = afterCallWorkDuration;

			const result = await api.updateTargetOutputData(targetId, targetOutputData);
			if (targetOutputData["__status__"] > 0 && result !== false && result !== null) {
				toast.push("Dáta boli uložené");
			}
		}
	}

	/**
	 * Ukončenie práce s klientom
	 *
	 * @param e
	 */
	async function exit(e: Event) {
		e.preventDefault();

		const result = await confirmation.show("<p>Naozaj chcete ukončit prácu s klientom?</p><p>Dáta budú uložené automaticky.</p>");

		if (result === true) {
			navigate("/main/summary");
		}
	}

	/**
	 * Zatvorenie modálneho okna - nový čas/náhradník
	 */
	function closeModal() {
		modal = false;
	}

	/**
	 * Nastavenie stavu hovoru na "Volať neskôr" ak bol nastavený čas na opakovanie hovoru
	 * @param value
	 */
	function setTargetStatus(status: string) {
		const select = document.querySelector("select[name=__status__]") as HTMLSelectElement;
		select.value = status;
	}

	function toggleTargetStatus(show: boolean) {
		const select = document.querySelector("select[name=__status__]") as HTMLSelectElement;

		if (!select) {
			return;
		}

		if (show === true) {
			select.style.display = "";
		} else {
			select.style.display = "none";
		}
	}

	/**
	 * Výpočet šíriek stĺpcov rozhrania
	 */
	function calculateColumns() {
		containerClass = "container";
		if (!target.crm_url || target.crm_as_link === "t") {
			if (!target.script) {
				formColWidth = 12;
			} else {
				formColWidth = 6;
				callScriptColWidth = 6;
			}
		} else {
			containerClass = "container-fluid";
			if (!target.script) {
				formColWidth = 3;
				crmColWidth = 9;
			} else {
				callScriptColWidth = 3;
				formColWidth = 2;
				crmColWidth = 7;
			}
		}
	}

	/**
	 * Spustenie akcií pri zmene url
	 * Načítanie informácii o klientovi, výpočet šíriek stĺpcov rozhrania a prípdne automatické prijatie hovoru
	 *
	 * @param params
	 */
	async function subscribeParams(params: { id: string, caller: string }) {
		let newTargetId: number = parseInt(params.id);
		if (targetId === newTargetId && target) return;

		clearInterval(callDurationInterval);
		callDurationInterval = null;

		clearInterval(afterCallWorkDurationInterval);
		afterCallWorkDurationInterval = null;

		dialerAgent.status.set(DialerAgentStatus.Ringing);

		const callId = get(dialerAgent.currentConnection).callId;

		let newTarget;

		if (newTargetId == 0) {
			newTarget = false;
		} else {
			newTarget = await api.getTarget(newTargetId);
		}

		if (newTarget === false || newTarget === null) {
			if (newTargetId != 0) {
				toast.push("Nepodarilo sa získať informácie o klientovi!");
			}
			navigate("/main/summary");
			return;
		}

		targetId = newTargetId;
		dialerAgent.target = target = newTarget;
		dialerAgent.caller = caller = params.caller;
		calculateColumns();

		enabledAfterCallWorkTime = secondsToTime(newTarget.after_call_work_length);

		if (unsubscribeDialerAgentStatus) {
			unsubscribeDialerAgentStatus();
		}

		const currentConnection = get(dialerAgent.currentConnection);

		if (currentConnection) {
			const callId2 = currentConnection.callId;
			console.debug("BEFORE:", callId, "AFTER:", callId2);

			if (callId === callId2) {
				unsubscribeDialerAgentStatus = dialerAgent.status.subscribe(subscribeDialerAgentStatus);

				if ($configuration.autoanswer === true) {
					answer();
				}
			}  else {
				console.warn("Different CALL_ID", "BEFORE:", callId, "AFTER:", callId2);
				navigate("/main/summary");
				return;
			}
		} else {
			console.warn("The connection was terminated while the client's information was being retrieved");
			navigate("/main/summary");
			return;
		}
	}

	async function subscribeDialerAgentStatus(status: DialerAgentStatus) {
		dialerAgentStatus = status;

		if (hangupCause === null && status === DialerAgentStatus.AfterCall) {
			if (target.enable_after_call_work === false) {
				navigate("/main/summary");
				return;
			}

			dialerAgent.status.set(DialerAgentStatus.AfterCallWork);
			if (caller === "dialeragent") {
				const targetDBData = await api.getTarget(targetId);
				if (targetDBData !== false) {
					if (targetDBData.show_in_agent === "t") {
						setTargetStatus(targetDBData.status.toString());
					} else {
						toggleTargetStatus(false);
					}
				}
			}

			clearInterval(afterCallWorkDurationInterval);
			afterCallWorkDurationInterval = null;
			afterCallWorkDurationInterval = setInterval(() => {
				afterCallWorkDuration++;
				afterCallWorkTimeExceedeed = afterCallWorkDuration > target.after_call_work_length;

				if (afterCallWorkTimeExceedeed === false) {
					return;
				}

				if (target.enable_extend_after_call_work === false) {
					navigate("/main/summary");
					return;
				} else {
					confirmation.show("Pracovný čas vypršal, chcete ho predĺžiť?").then((result) => {
						if (result === true) {
							target.after_call_work_length = Number.MAX_SAFE_INTEGER;
							return;
						} else {
							navigate("/main/summary");
							return;
						}
					});
				}
			}, 1000);

			return;
		}

		clearInterval(callDurationInterval);
		callDurationInterval = null;
		if (status === DialerAgentStatus.Talk) {
			callReceived = true;
			callDurationInterval = setInterval(() => {
				callDuration++;
			}, 1000);
		}
	}

	/**
	 * Nastavovanie hlasitosti výstupného zvukového zariadenia
	 *
	 * @param volume
	 */
	function setVolume(volume: number) {
		const audio = document.getElementById("remote-audio") as HTMLAudioElement;
		audio.volume = volume;
	}

	function setTimeScheduled(_timeScheduled: string) {
		timeScheduled = _timeScheduled;
		setTargetStatus("13");
	}

	function setTargetAlternate(_targetAlternate: TargetAlternate) {
		targetAlternate = _targetAlternate;
		setTargetStatus("10");
	}

	/**
	 * Nastavenie východzích hodnôt
	 */
	onMount(() => {
		if (!get(dialerAgent.currentConnection)) {
			navigate("/main/summary");
			return;
		}

		unsubscribeParams = params.subscribe(subscribeParams);

		modal = false;
		modalSaveCallback = null;

		targetId = null;
		target = null;

		dialerAgentStatus = undefined;
		afterCallWorkTimeExceedeed = false;
		enabledAfterCallWorkTime = undefined;

		callDurationInterval = null;
		callDuration = 0;
		formattedCallDuration = "00:00:00";

		afterCallWorkDurationInterval = null;
		afterCallWorkDuration = 0;
		formattedAfterCallWorkDuration = "00:00:00";

		callScriptColWidth = undefined;
		formColWidth = undefined;
		crmColWidth = undefined;
		containerClass = undefined;

		setDefaultStatus = true;

		timeScheduled = null;
		targetAlternate = null;

		callIsDeclined = false;
		error = false;
	});

	/**
	 * Spustenie akcií pri aktualizácii komponentu
	 */
	afterUpdate(() => {
		const form = document.getElementById("target-data") as HTMLFormElement;
		if (form && setDefaultStatus === true) {
			setTargetStatus("10");
			setDefaultStatus = false;
		}
	});

	/**
	 * Spustenie akcií pri opustení práce s klientom
	 * ukončenie odpočítavaní, odpauzovanie agenta
	 */
	onDestroy(() => {
		clearInterval(callDurationInterval);
		callDurationInterval = null;

		clearInterval(afterCallWorkDurationInterval);
		afterCallWorkDurationInterval = null;

		hangup();
		if (paused === true) {
			paused = false;
			api.unpause();
		}

		if (unsubscribeParams) {
			unsubscribeParams();
		}

		if (unsubscribeDialerAgentStatus) {
			unsubscribeDialerAgentStatus();
		}

		if (
			callReceived === true
			&& error === false
			&& $currentConnection
			&& $currentConnection.sipClient
			&& $currentConnection.sipClient.callCompleteElsewhere === false
		) {
			send();
		}

		dialerAgent.currentConnection.set(null);
	});
</script>

<svelte:head>
	<title>Klient kampane | DialerAgent</title>
</svelte:head>

{#if target}
	<div class="container">
		<div class="alert alert-dark mt-3">
			<h2>Kampaň: {target.campaign_name}</h2>

			<div class="d-flex justify-content-between">
				<span class="h3 target-info">
					Klient: {target.name}, {target.telno} ({#if caller === "inbound"}in{:else}out{/if})
					{#if target.time_scheduled}
					<strong>Hovor naplánovaný na {target.time_scheduled}</strong>
					{/if}
					{#if target.replace_target_name !== null}
					<em>Náhradník za {target.replace_target_name} ({target.replace_target_telno})</em>
					{/if}
				</span>
				<span>
					<div>Trvanie hovoru: {formattedCallDuration}</div>
					<div>Pracovný čas: {formattedAfterCallWorkDuration}</div>
				</span>
			</div>

			{#if $configuration.autoanswer === false && dialerAgentStatus === DialerAgentStatus.Ringing}<button class="btn btn-info" on:click={answer}>Prijať hovor</button>{/if}
			{#if $configuration.autoanswer === false && dialerAgentStatus === DialerAgentStatus.Ringing}<button class="btn btn-info" on:click={decline}>Zamietnuť hovor</button>{/if}
			{#if dialerAgentStatus === DialerAgentStatus.Talk}<button class="btn btn-danger" on:click={answeringMachine}  disabled={disabledAnswerMachine}>Ukončiť (Odkazovač)</button>{/if}
			{#if dialerAgentStatus === DialerAgentStatus.Talk}<button class="btn btn-info" on:click={() => hangup()}>Ukončiť hovor</button>{/if}
			{#if dialerAgentStatus === DialerAgentStatus.AfterCallWork}<button class="btn btn-info" on:click={exit}>Koniec</button>{/if}
			<button
				class="btn btn-info"
				on:click={() => {
					modal = TimeScheduled;
					modalSaveCallback = setTimeScheduled;
				}}>Volať neskôr</button
			>
			<button
				class="btn btn-info"
				on:click={() => {
					modal = CallAlternate;
					modalSaveCallback = setTargetAlternate;
				}}>Volať náhradníka</button
			>

			{#if target.crm_url && target.crm_as_link === "t"}
				<a href={target.crm_url} class="btn btn-danger" target="_blank">CRM</a>
			{/if}
		</div>

		{#if dialerAgentStatus === DialerAgentStatus.Talk}
			<div class="alert alert-secondary">
				<div class="d-flex">
					<div class="pe-4">
						<i class="fas fa-volume-up" />
					</div>
					<input type="range" min="0" max="1" bind:value={volume} step="0.025" class="form-range" name="volume" />
				</div>
			</div>
		{/if}

		{#if afterCallWorkTimeExceedeed === true}
			<div class="alert alert-danger mt-3">
				<p class="mb-0">Prekročili ste povolený pracovný čas {enabledAfterCallWorkTime}!</p>
			</div>
		{/if}
	</div>

	<div class={containerClass}>
		<div class="row">
			{#if target.script}
				<div class="col-{callScriptColWidth}">
					<div class="card">
						<div class="card-header bg-primary">Callscript</div>
						<div class="card-body">
							{@html target.script}
						</div>
					</div>
				</div>
			{/if}

			{#if target.form}
				<div class="col-{formColWidth}">
					<div class="card">
						<div class="card-header bg-primary">Doplňujúce informácie o klientovi</div>
						<div class="card-body">
							<form id="target-data">
								{@html target.form}
							</form>
						</div>
					</div>
				</div>
			{/if}

			{#if target.crm_url && target.crm_as_link === "f"}
				<div class="col-{crmColWidth}">
					<div class="card">
						<div class="card-header bg-primary">CRM</div>
						<div class="card-body">
							<iframe src={target.crm_url} title="CRM" width="100%" height="600" />
						</div>
					</div>
				</div>
			{/if}
		</div>
	</div>

	<svelte:component this={modal} {closeModal} {target} saveCallback={modalSaveCallback} />
{/if}

<style>
	.target-info strong,
	.target-info em {
		display: block;
		font-size: 50%;
	}
</style>